import React, { useEffect, useRef, useState } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Avatar, AVATAR_SIZE } from '@depop/web-ui-kit/Avatar';
import { Button } from '@depop/web-ui-kit/Button';
import { ArrowForwardIcon } from '@depop/web-ui-kit/Icons/ArrowForwardIcon';
import { CancelIcon } from '@depop/web-ui-kit/Icons/CancelIcon';
import { FacebookIcon } from '@depop/web-ui-kit/Icons/FacebookIcon';
import { IconDepopIcon } from '@depop/web-ui-kit/Icons/IconDepopIcon';
import { InstagramIcon } from '@depop/web-ui-kit/Icons/InstagramIcon';
import { TiktokIcon } from '@depop/web-ui-kit/Icons/TiktokIcon';
import { TwitterIcon } from '@depop/web-ui-kit/Icons/TwitterIcon';
import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useClickAway } from 'react-use';

import styles from './styles.module.css';

import { useAppBadgesMethods } from '@/components/AppBadge/useAppBadgesMethods.ts';
import { AppDownloadCard } from '@/components/AppDownloadCard';
import { ExtendedLink } from '@/components/ExtendedLink';
import { SubMenu } from '@/components/MobileNavigation/SubMenu';
import { SelectLocationWithInlineLabel } from '@/components/SelectLocation';
import { SOCIAL_MEDIA_SITES } from '@/constants/externalUrls';
import {
  ActivityTrackerEventType,
  NavCtaActionTypes,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { APP_STORE_URL } from '@/modules/branch/constants';
import { DeviceType } from '@/modules/device/types';
import { useUserAgent } from '@/modules/device/useUserAgent';
import { useBranchAppLink } from '@/modules/externalTracking/useBranchAppLink';
import { getLoginRedirectParams } from '@/modules/login/helpers';
import { useNavConfig } from '@/modules/meganav/useNavConfig';
import { ROUTE_CHANGE_START_EVENT } from '@/modules/routing/constants';
import { useSellNowHref } from '@/modules/signup/hooks/useSellNowHref';
import { useSignup } from '@/modules/signup/hooks/useSignup';
import { useCurrentUser } from '@/modules/user/useCurrentUser';
import { useUserCounter } from '@/modules/user/useUserCounter';

export type MobileMenuProps = {
  onClose: () => void;
  handleLogout: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isOpen: boolean;
  secondaryNav: React.ReactNode;
  showAppDownloadExperiment: boolean;
};

export function MobileNavigationMenu({
  isOpen,
  onClose,
  secondaryNav,
  handleLogout,
  showAppDownloadExperiment,
}: MobileMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const firstButtonElement = useRef<HTMLButtonElement>(null);

  const enableMobileMenu = isOpen;

  const t = useTranslations('common');
  const [authenticated, currentUser] = useCurrentUser();
  const { url: signupUrl } = useSignup();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { href } = useSellNowHref();
  const { device } = useUserAgent();
  const branchAppLink = useBranchAppLink();

  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const currentPath = searchParams.size
    ? `${pathname}?${searchParams}`
    : pathname;

  const { config } = useNavConfig();
  const [activeSubMenu, setActiveSubMenu] = useState('');

  const { getIosAppBadge, getAndroidAppBadge } = useAppBadgesMethods();

  const { data: userCounters } = useUserCounter();

  const offersCount = userCounters?.seller_offers_to_review || 0;

  function getAppDownloadLink() {
    switch (device) {
      case DeviceType.IOS:
        return getIosAppBadge().href;
      case DeviceType.ANDROID:
        return getAndroidAppBadge().href;
      default:
        return APP_STORE_URL;
    }
  }

  function handleSignupClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: NavCtaActionTypes.NAV_SIGNUP_CTA,
    });
  }

  function handleLoginClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: NavCtaActionTypes.NAV_LOGIN_CTA,
    });
  }

  function handleSubMenuBackClick() {
    setActiveSubMenu('');
  }

  function handleListItemClick(label: string) {
    setActiveSubMenu(label);
  }

  function handleLogoClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: NavCtaActionTypes.DEPOP_LOGO_CTA,
    });
    onClose();
  }

  useEffect(() => {
    if (menuRef && menuRef.current) {
      if (enableMobileMenu) {
        menuRef.current.classList.remove(
          styles['mobileMenuWrapper--slideOut'],
          styles['mobileMenuWrapper--hidden']
        );
        menuRef.current.classList.add(styles['mobileMenuWrapper--slideIn']);
      } else {
        menuRef.current.classList.remove(styles['mobileMenuWrapper--slideIn']);
        menuRef.current.classList.add(styles['mobileMenuWrapper--slideOut']);
        /*
          We need to hide the menu or else it interferes with some elements in the page.
          For example, it becomes impossible to click on the text inputs on the login screen.
          The transition time has been set to 250ms in the styles, so a 300ms timeout for hiding
          allows us time to complete the transition. Otherwise, leaving the menu visible conflicts
          with some of the focus code and messes up tabbing.
        */
        setTimeout(() => {
          if (menuRef && menuRef.current) {
            menuRef.current.classList.add(styles['mobileMenuWrapper--hidden']);
          }
        }, 300);
      }
    }
  });

  useEffect(() => {
    document.addEventListener(ROUTE_CHANGE_START_EVENT, onClose);

    return () => {
      document.removeEventListener(ROUTE_CHANGE_START_EVENT, onClose);
    };
  }, []);

  useClickAway(menuRef, () => {
    if (isOpen) {
      onClose();
    }
  });

  function renderGetTheAppDeeplink() {
    if (authenticated) {
      return null;
    }
    return (
      <ExtendedLink href={branchAppLink}>
        <Body variant="sm-regular" className={styles.getTheAppLink}>
          {t('Nav.Download')}
        </Body>
      </ExtendedLink>
    );
  }

  return (
    <div
      className={styles.mobileMenuWrapper}
      role="dialog"
      aria-label="Main menu"
      ref={menuRef}
    >
      {isOpen && (
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <ExtendedLink
                href="/"
                onClick={handleLogoClick}
                aria-label="Depop Logo"
              >
                <IconDepopIcon className={styles.depopLogo} />
              </ExtendedLink>
              <button
                className={styles.closeButton}
                aria-label={t('Close')}
                onClick={onClose}
                ref={firstButtonElement}
              >
                <CancelIcon />
              </button>
            </div>
            {!authenticated && (
              <div className={styles.authButtons}>
                <ExtendedLink href={href}>
                  <Button as="span" onClick={handleSignupClick} block>
                    {t('Nav.SellNow')}
                  </Button>
                </ExtendedLink>
                <ExtendedLink href={signupUrl}>
                  <Button as="span" onClick={handleSignupClick} block outline>
                    {t('Nav.SignUp')}
                  </Button>
                </ExtendedLink>
                <ExtendedLink
                  href={`/login/${getLoginRedirectParams(
                    searchParams.get('redirect') || currentPath
                  )}`}
                >
                  <Button as="span" onClick={handleLoginClick} block outline>
                    {t('Nav.Login')}
                  </Button>
                </ExtendedLink>
              </div>
            )}
          </div>
          <ul
            className={clsx(styles.primaryNav, {
              [styles['primaryNav--showBorderTop']]: !authenticated,
              [styles['primaryNav--showBorderBottom']]:
                showAppDownloadExperiment,
            })}
          >
            {config.map((item) => (
              <li key={item.label} className={styles.navListItem}>
                <button
                  className={styles.navListItemButton}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={
                    item.label === activeSubMenu ? 'true' : 'false'
                  }
                  onClick={() => handleListItemClick(item.label)}
                >
                  <Body
                    variant="sm-bold"
                    className={styles.navListItemLabel}
                    as="span"
                    style={
                      item.colourOverride
                        ? ({
                            '--textColor': item.colourOverride,
                          } as React.CSSProperties)
                        : {}
                    }
                  >
                    {item.label}
                  </Body>
                  <ArrowForwardIcon />
                </button>
                <SubMenu
                  closeMenu={onClose}
                  onBackClick={handleSubMenuBackClick}
                  isOpen={item.label === activeSubMenu}
                  items={item.contentCollection.items}
                  title={item.label}
                />
              </li>
            ))}
          </ul>
          {showAppDownloadExperiment && (
            <AppDownloadCard
              subtitle={t('Nav.AppDownload')}
              placement="burger_menu"
              href={getAppDownloadLink()}
              className={styles.appDownloadBox}
            />
          )}
          {authenticated && (
            <ul className={styles.authenticatedNav}>
              <li key="mobile-authed-profile" className={styles.navListItem}>
                <ExtendedLink
                  href="/[username]/"
                  as={`/${currentUser?.username}/`}
                >
                  <Body variant="sm-regular" as="span">
                    {t('Nav.Profile')}
                  </Body>
                  <Avatar
                    src={currentUser?.picture_url}
                    initials={currentUser?.initials || ''}
                    verified={currentUser?.verified}
                    size={AVATAR_SIZE.EXTRA_SMALL}
                  />
                </ExtendedLink>
              </li>
              <li key="mobile-authed-sell" className={styles.navListItem}>
                <ExtendedLink href={href}>
                  <Body variant="sm-regular" as="span">
                    {t('Nav.Sell')}
                  </Body>
                </ExtendedLink>
              </li>
              <li
                key="mobile-authed-selling-hub"
                className={styles.navListItem}
              >
                <ExtendedLink href="/sellinghub/" as="/sellinghub/">
                  <Body variant="sm-regular" as="span">
                    {t('Nav.YourSellingHubWithoutOffers')}
                  </Body>
                  {!!offersCount && (
                    <div className={styles.offerAlertContainer}>
                      <span
                        className={styles.offerAlertNotificationContainer}
                      />
                      <Body
                        variant="sm-regular"
                        className={styles.offerAlertText}
                      >
                        {t('Nav.NewOffers', {
                          count: offersCount,
                        })}
                      </Body>
                    </div>
                  )}
                </ExtendedLink>
              </li>
              <li key="mobile-authed-purchases" className={styles.navListItem}>
                <ExtendedLink href="/purchases/">
                  <Body variant="sm-regular" as="span">
                    {t('Nav.BuyersReceipts')}
                  </Body>
                </ExtendedLink>
              </li>
              <li key="mobile-authed-settings" className={styles.navListItem}>
                <ExtendedLink href="/settings/">
                  <Body variant="sm-regular" as="span">
                    {t('Nav.Settings')}
                  </Body>
                </ExtendedLink>
              </li>
              <li key="mobile-authed-disputes" className={styles.navListItem}>
                <ExtendedLink href="/disputes/">
                  <Body variant="sm-regular" as="span">
                    {t('Nav.Drc')}
                  </Body>
                </ExtendedLink>
              </li>
            </ul>
          )}
          <div className={styles.secondaryNavWrapper}>
            {secondaryNav}
            {renderGetTheAppDeeplink()}
          </div>
          <div className={styles.footer}>
            <div className={styles.socialLinks}>
              <a
                href={SOCIAL_MEDIA_SITES.INSTAGRAM}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.socialLink}
              >
                <InstagramIcon color="#262626" />
              </a>
              <a
                href={SOCIAL_MEDIA_SITES.X}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.socialLink}
              >
                <TwitterIcon color="#262626" />
              </a>
              <a
                href={SOCIAL_MEDIA_SITES.FACEBOOK}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.socialLink}
              >
                <FacebookIcon color="#262626" />
              </a>
              <a
                href={SOCIAL_MEDIA_SITES.TIKTOK}
                target="_blank"
                rel="noreferrer noopener"
                className={styles.socialLink}
              >
                <TiktokIcon title="tiktok" />
              </a>
            </div>
            <div className={styles.selectWrapper}>
              <SelectLocationWithInlineLabel label={t('Nav.SelectLocation')} />
            </div>
            {authenticated && (
              <button onClick={handleLogout} className={styles.logoutButton}>
                <Heading variant="sm">{t('Nav.Logout')}</Heading>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
